import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Check, Lock, LogOut, Pencil, Trophy, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TeamCard } from "../components/PlayerCard";
import { AchievementGrid } from "../components/AchievementGrid";
import RankedStatsCard from "../components/RankedStatsCard";
import { ModeratorBadge } from "../components/ModeratorBadge";
import { UserName } from "../components/UserName";
import Avatar from "../components/Avatar";
import { countryName } from "../lib/countries";
import CountryPicker from "../components/CountryPicker";
import { countryFlag, fmtMs } from "../lib/derive";
import { toast } from "sonner";

/* Reusable team picker (uses /api/teams/search). */
function TeamPicker({ initial, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(initial || null);
  const timer = useRef(null);

  useEffect(() => {
    setPicked(initial || null);
  }, [initial]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/teams/search?q=${encodeURIComponent(q)}&limit=10`);
        setResults(data);
      } catch (_) {
        setResults([]);
      }
    }, 200);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 flex-wrap">
        {picked && (
          <div className="flex items-center gap-2 rounded-full bg-[var(--surface-2)] border border-[var(--accent)]/30 px-3 py-2">
            {picked.logo_url ? (
              <img src={picked.logo_url} alt="" className="w-5 h-5 object-contain" referrerPolicy="no-referrer" />
            ) : (
              <span className="w-5 h-5 rounded-full" style={{ background: picked.badge_color || "#888" }} />
            )}
            <span className="text-sm font-semibold">{picked.name}</span>
            <button
              onClick={() => {
                setPicked(null);
                onPick(null);
              }}
              data-testid="profile-club-clear"
              className="text-xs text-zinc-500 hover:text-zinc-200 ml-1"
              type="button"
              aria-label="Clear favourite club"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <input
          data-testid="profile-club-search"
          type="text"
          value={q}
          placeholder={picked ? "change club…" : "search clubs…"}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          className="flex-1 min-w-[200px] px-4 py-2.5 rounded-full bg-[var(--surface)] border border-white/10 focus:border-[var(--accent)] outline-none text-sm transition"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-2 w-full max-h-64 overflow-auto rounded-xl border border-white/10 bg-[var(--surface)] shadow-2xl">
          {results.map((t) => (
            <button
              key={t._id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setPicked(t);
                onPick(t);
                setOpen(false);
                setQ("");
              }}
              data-testid={`profile-club-result-${t._id}`}
              className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-[var(--accent)]/10 transition"
            >
              {t.logo_url ? (
                <img src={t.logo_url} alt="" className="w-5 h-5 object-contain" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-5 h-5 rounded-full" style={{ background: t.badge_color || "#888" }} />
              )}
              <span className="text-sm flex-1 truncate">{t.name}</span>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
                {t.type === "national_team" ? "NT" : t.country}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Avatar editor — preview + file upload + remove. */
function AvatarEditor({ value, user, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";  // allow re-uploading same file
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp|gif)$/i.test(f.type)) {
      toast.error("Use a PNG, JPEG, WEBP or GIF image.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.user);
      toast.success("Avatar updated");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/me/avatar");
      onUploaded(data.user);
      toast.success("Avatar removed");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  // value may be a relative path like `/api/uploads/avatars/{id}.png?v=…`.
  // Prefix REACT_APP_BACKEND_URL when relative so the image loads from the
  // correct host both locally and behind k8s ingress.
  const resolvedSrc = useMemo(() => {
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("/api/")) {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      return `${base}${value}`;
    }
    return value;
  }, [value]);

  return (
    <div className="relative">
      <div className="relative">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="rounded-full ring-2 ring-[var(--accent)]/40 shadow-[0_0_30px_-5px_var(--accent)]"
          data-testid="profile-avatar"
        >
          <Avatar
            user={{
              ...(user || {}),
              avatar_url: resolvedSrc || (user && user.avatar_url) || null,
            }}
            size={112}
          />
        </motion.div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="profile-avatar-edit"
          aria-label="Edit avatar"
          className="absolute -bottom-1 -left-1 w-9 h-9 rounded-full bg-[var(--surface-2)] border-2 border-[var(--bg)] flex items-center justify-center text-zinc-200 hover:text-[var(--accent)] transition"
        >
          <Camera size={14} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          onChange={onFile}
          className="hidden"
          data-testid="profile-avatar-file"
        />
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute z-20 top-full mt-3 left-0 w-[280px] sm:w-[320px] rounded-2xl border border-white/10 bg-[var(--surface)] p-4 shadow-2xl"
        >
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Profile picture</div>
          <p className="text-xs text-zinc-400 mb-3">
            PNG, JPEG, WEBP or GIF — up to 5 MB. We crop to a square and resize to 512×512.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onPick}
              disabled={busy}
              data-testid="profile-avatar-upload"
              className="flex-1 text-xs font-bold uppercase tracking-widest text-black bg-[var(--accent)] px-3 py-2 rounded-full disabled:opacity-50"
            >
              {busy ? "uploading…" : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={onRemove}
                disabled={busy}
                data-testid="profile-avatar-remove"
                className="text-xs uppercase tracking-widest text-red-300 hover:text-red-200 px-3 py-2 rounded-full border border-red-500/30 disabled:opacity-50"
              >
                remove
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 w-full text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
          >
            close
          </button>
        </motion.div>
      )}
    </div>
  );
}

function MatchHistoryRow({ m, currentUserId, index }) {
  const date = m.finished_at ? new Date(m.finished_at) : null;
  const dateStr = date ? date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }) : "—";
  const players = (m.players || []).slice().sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      data-testid={`match-row-${m.id}`}
      className={`grid grid-cols-[60px_1fr_auto] sm:grid-cols-[80px_1fr_140px_80px] gap-3 items-center px-4 py-3 rounded-xl border ${
        m.won
          ? "bg-[var(--accent)]/8 border-[var(--accent)]/30"
          : "bg-[var(--surface)] border-white/5"
      }`}
    >
      <div className="text-[11px] text-zinc-500 uppercase tracking-widest">{dateStr}</div>
      <div className="min-w-0">
        <div className="text-xs text-zinc-400 truncate flex items-center gap-1 flex-wrap">
          <span>vs</span>
          {(() => {
            const opps = players.filter((p) => p.user_id !== currentUserId);
            if (opps.length === 0) return <span>—</span>;
            return opps.map((p, idx) => (
              <span key={p.user_id || idx} className="inline-flex items-center">
                <UserName user={p} hideModBadge />
                {idx < opps.length - 1 && <span>,</span>}
              </span>
            ));
          })()}
        </div>
        <div className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">
          {m.settings?.difficulty || "—"} · {m.settings?.rounds_count || "?"} rounds
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-widest">
        {m.won ? (
          <span className="text-[var(--accent)] flex items-center gap-1 font-bold">
            <Trophy size={12} /> winner
          </span>
        ) : (
          <span className="text-zinc-500">
            #{m.my_rank || "—"} of {players.length}
          </span>
        )}
      </div>
      <div className="text-right font-mono font-black tabular-nums text-[var(--text)]">{m.my_score ?? 0}</div>
    </motion.li>
  );
}

function StatChip({ label, value, suffix }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[var(--surface)] px-3 py-2.5">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-lg font-black tabular-nums mt-0.5">
        {value}
        {suffix && <span className="text-xs text-zinc-500 font-bold ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [achievements, setAchievements] = useState({ unlocked: [], locked: [] });

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [country, setCountry] = useState("");
  const [club, setClub] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meRes, mhRes, achRes] = await Promise.all([
          api.get("/me"),
          api.get("/me/match-history?limit=10").catch(() => ({ data: { matches: [] } })),
          api.get("/me/achievements").catch(() => ({ data: { unlocked: [], locked: [] } })),
        ]);
        if (cancelled) return;
        setMe(meRes.data);
        setUsername(meRes.data.user.username || "");
        setEmail(meRes.data.user.email || "");
        setAvatarUrl(meRes.data.user.avatar_url || "");
        setCountry(meRes.data.user.country || "");
        setClub(meRes.data.favorite_club || null);
        setMatches(mhRes.data.matches || []);
        setAchievements({ unlocked: achRes.data.unlocked || [], locked: achRes.data.locked || [] });
      } catch (_) {
        // ignore
      } finally {
        if (!cancelled) setMatchesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const payload = {
        country,
        favorite_club_id: club?._id || "",
      };
      const { data } = await api.patch("/me", payload);
      setUser(data.user);
      setMe((prev) => ({ ...(prev || {}), user: data.user, favorite_club: club }));
      setSaved(true);
      toast.success("Profile saved");
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Save failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = useMemo(() => {
    const s = (username || user?.email || "?").toUpperCase();
    return s[0];
  }, [username, user]);

  const stats = me?.user_stats || {
    games_played: 0,
    wins: 0,
    total_correct: 0,
    total_answers: 0,
    avg_response_ms: 0,
    best_streak: 0,
    current_streak: 0,
    football_iq: 0,
  };
  const winRate = stats.games_played ? Math.round((stats.wins / stats.games_played) * 100) : 0;
  const accuracy = stats.total_answers ? Math.round((stats.total_correct / stats.total_answers) * 100) : 0;
  const flag = countryFlag(country);

  return (
    <div data-testid="profile-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="px-6 py-5 max-w-4xl mx-auto flex items-center justify-between">
        <Link
          to="/dashboard"
          className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] hover:brightness-125 transition"
        >
          ← dashboard
        </Link>
        <button
          onClick={onLogout}
          data-testid="profile-logout"
          className="text-sm px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 transition inline-flex items-center gap-2"
        >
          <LogOut size={14} /> Log out
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-16 space-y-10">
        {/* HERO */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start"
        >
          <AvatarEditor
            value={avatarUrl}
            user={user}
            onUploaded={(u) => {
              setAvatarUrl(u.avatar_url || "");
              setUser(u);
              setMe((prev) => ({ ...(prev || {}), user: u }));
            }}
          />

          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">your profile</div>
            <h1
              className="text-4xl sm:text-5xl font-black mt-1.5 leading-tight truncate flex items-center gap-2"
              data-testid="profile-username-display"
            >
              {me?.user ? <UserName user={me.user} /> : (username || "player")}
              {flag && (
                <span
                  className="ml-3 text-3xl align-middle"
                  title={countryName(country)}
                  aria-label={countryName(country) || undefined}
                >
                  {flag}
                </span>
              )}
            </h1>
            <div className="text-sm text-zinc-500 mt-1 truncate">
              {user?.email} ·{" "}
              <span className="text-zinc-400">
                {user?.role === "admin" ? "Admin" : "Player"}
              </span>{" "}
              ·{" "}
              <span className="text-zinc-400">
                {user?.auth_provider === "google" ? "Google sign-in" : "Email"}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div
                data-testid="profile-iq-chip"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent)]/10 border border-[var(--accent)]/30"
              >
                <Trophy size={12} className="text-[var(--accent)]" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-400">
                  Football IQ
                </span>
                <span className="text-sm font-black text-[var(--accent)] tabular-nums">
                  {stats.football_iq}
                </span>
              </div>
            </div>
          </div>
        </motion.section>

        {/* RANKED SECTION */}
        <RankedStatsCard ranked={me?.ranked} />

        {/* STATS STRIP */}
        <section className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <StatChip label="Games" value={stats.games_played} />
          <StatChip label="Win %" value={winRate} suffix="%" />
          <StatChip label="Accuracy" value={accuracy} suffix="%" />
          <StatChip label="Best streak" value={stats.best_streak} />
          <StatChip label="Curr streak" value={stats.current_streak} />
          <StatChip label="Avg" value={fmtMs(stats.avg_response_ms)} />
        </section>

        {/* EDIT FORM */}
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          onSubmit={save}
          className="rounded-2xl border border-white/10 bg-[var(--surface)] p-6 space-y-6"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Pencil size={14} className="text-[var(--accent)]" /> Edit profile
            </h2>
            {saved && (
              <motion.span
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                data-testid="profile-saved"
                className="text-[11px] uppercase tracking-widest text-[var(--accent)] inline-flex items-center gap-1.5 font-bold"
              >
                <Check size={12} /> saved
              </motion.span>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                <Lock size={10} /> Username (permanent)
              </label>
              <div
                data-testid="profile-username-locked"
                className="mt-1 w-full px-4 py-3 rounded-lg bg-black/40 border border-white/5 text-zinc-400 flex items-center justify-between"
              >
                <span className="font-mono flex items-center gap-1.5">{me?.user ? <UserName user={me.user} /> : username}</span>
                <Lock size={12} className="text-zinc-600" />
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">Locked at registration — cannot be changed.</div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                <Lock size={10} /> Email (permanent)
              </label>
              <div
                data-testid="profile-email-locked"
                className="mt-1 w-full px-4 py-3 rounded-lg bg-black/40 border border-white/5 text-zinc-400 flex items-center justify-between"
              >
                <span className="truncate">{email}</span>
                <Lock size={12} className="text-zinc-600 shrink-0 ml-2" />
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">Locked at registration — cannot be changed.</div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Country</label>
              <CountryPicker
                value={country}
                onChange={setCountry}
                testId="profile-country"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Favourite club</label>
              <div className="mt-1">
                <TeamPicker initial={club} onPick={setClub} />
              </div>
              {club && (
                <div className="mt-3 max-w-sm">
                  <TeamCard team={club} testId="profile-favourite-club-preview" />
                </div>
              )}
            </div>
          </div>

          {err && (
            <div
              data-testid="profile-error"
              className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
            >
              {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/5">
            <button
              type="submit"
              disabled={busy}
              data-testid="profile-save"
              className="px-6 py-2.5 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-50 transition shadow-[0_8px_30px_-12px_var(--accent)]"
            >
              {busy ? "saving…" : "Save changes"}
            </button>
          </div>
        </motion.form>

        {/* ACHIEVEMENTS */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-500">achievements</h2>
            <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
              {achievements.unlocked.length} / {achievements.unlocked.length + achievements.locked.length || 7}
            </span>
          </div>
          <AchievementGrid unlocked={achievements.unlocked} locked={achievements.locked} />
        </section>

        {/* MATCH HISTORY */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-500">recent matches</h2>
            <span className="text-[10px] text-zinc-600 uppercase tracking-widest">last 10</span>
          </div>
          {matchesLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-14 rounded-xl border border-white/5 bg-[var(--surface)] animate-pulse"
                />
              ))}
            </div>
          ) : matches.length === 0 ? (
            <div
              data-testid="match-history-empty"
              className="rounded-2xl border border-dashed border-white/10 bg-[var(--surface)]/40 p-8 text-center"
            >
              <div className="text-base font-bold text-zinc-300">No matches yet.</div>
              <div className="text-xs text-zinc-500 mt-1">
                Create or join a lobby and your finishes will land here.
              </div>
            </div>
          ) : (
            <ul data-testid="match-history-list" className="space-y-2">
              {matches.map((m, i) => (
                <MatchHistoryRow key={m.id} m={m} currentUserId={user?.id} index={i} />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
